import { DEFAULT_AGENT_GROUNDING } from '@/constants/agent/defaults';
import { DEFAULT_VOICE_AGENTS, DEFAULT_VOICE_CONFIG, GenerationError } from '@/state/presenters';
import { ValueOrUpdater } from '@/utils/types';
import {
    AgentCreateTabType,
    AgentGrounding,
    AgentKnowledgeSource,
    IAgent,
    ImageType,
    IVoice,
    LLM,
    Triggers,
    VoiceConfig,
} from '@d-id/types';
import { action, computed, observable } from 'mobx';
import { RootStore } from '../root';
import { AgentFileS3UploadStatus, AgentPageMode, AgentShareActiveTab, InitAgentState, InputAgent, UploadingFile } from './types';

export enum KnowledgeErrorType {
    FILES = 'FILES',
    INPUT_TEXT = 'INPUT_TEXT',
}

export interface Errors {
    ttsError: GenerationError | null;
    missingFilesError: string | null;
    instructionsTooLong: string | null;
    knowledgeTooLong: string | null;
    missingInputTextKnowledgeError: string | null;
}

export interface UI {
    isPromptCustomizationPopoverOpen: boolean;
}

export const INITIAL_VALUES = {
    currentTab: AgentCreateTabType.Appearance,
    errorTabs: [] as AgentCreateTabType[],
    inputAgent: {} as InputAgent,
    llm: {
        template: DEFAULT_AGENT_GROUNDING,
    } as LLM,
    triggers: {} as Triggers,
    uploadingDocumentFiles: [] as UploadingFile[],
    deletedDocumentFiles: [] as UploadingFile[],
    isUpdatingKnowledge: false,
    isCreating: false,
    pageMode: AgentPageMode.Gallery,
    shareActiveTab: AgentShareActiveTab.Embed,
    isCodeSnippetCopied: false,
    greetings: [] as string[],
    starterQuestions: [] as string[],
    baseKnowledge: null as string | null,
    isStarterQuestionsEdited: false,
    initAgentState: {} as InitAgentState,
    presenterIsStitch: true,
    isPlaygroundLoaded: false,
    isEditorLoading: true,
    isEditingDraftAgent: false,
    selectedPresenter: '',
    selectedPresenterType: ImageType.Upload,
    presenterBackgroundSourceUrl: '',
    presenterBackgroundColor: '',
    presenterBackgroundThumbnailUrl: '',
    backgroundProcessingPresenters: new Set<string>(),
    errors: {
        ttsError: null,
        missingFilesError: null,
        missingInputTextKnowledgeError: null,
    } as Errors,
    ttsVoiceConfig: DEFAULT_VOICE_CONFIG,
    ttsSelectedLanguage: DEFAULT_VOICE_AGENTS.language,
    ttsSelectedVoice: null,
    isVoiceManuallySelected: false,
    ui: {
        isPromptCustomizationPopoverOpen: false,
    } as UI,
    showUpgradeModal: false,
    isAgentEdit: false,
} as const;

export class AgentEditorStore {
    root: RootStore;

    @observable accessor currentTab: AgentCreateTabType = INITIAL_VALUES.currentTab;
    @observable accessor errorTabs: AgentCreateTabType[] = [...INITIAL_VALUES.errorTabs];
    @observable accessor inputAgent: InputAgent = { ...INITIAL_VALUES.inputAgent };
    @observable accessor llm: LLM = { ...INITIAL_VALUES.llm };
    @observable accessor triggers: Triggers = { ...INITIAL_VALUES.triggers };
    @observable accessor uploadingDocumentFiles: UploadingFile[] = [...INITIAL_VALUES.uploadingDocumentFiles];
    @observable accessor deletedDocumentFiles: UploadingFile[] = [...INITIAL_VALUES.deletedDocumentFiles];
    @observable accessor isUpdatingKnowledge: boolean = INITIAL_VALUES.isUpdatingKnowledge;
    @observable accessor isCreating: boolean = INITIAL_VALUES.isCreating;
    @observable accessor pageMode: AgentPageMode = INITIAL_VALUES.pageMode;
    @observable accessor shareActiveTab: AgentShareActiveTab = INITIAL_VALUES.shareActiveTab;
    @observable accessor isCodeSnippetCopied: boolean = INITIAL_VALUES.isCodeSnippetCopied;
    @observable accessor greetings: string[] = [...INITIAL_VALUES.greetings];
    @observable accessor starterQuestions: string[] = [...INITIAL_VALUES.starterQuestions];
    @observable accessor baseKnowledge: string | null = INITIAL_VALUES.baseKnowledge;
    @observable accessor isStarterQuestionsEdited: boolean = INITIAL_VALUES.isStarterQuestionsEdited;
    @observable accessor initAgentState: InitAgentState = { ...INITIAL_VALUES.initAgentState };
    @observable accessor presenterIsStitch: boolean = INITIAL_VALUES.presenterIsStitch;
    @observable accessor isPlaygroundLoaded: boolean = INITIAL_VALUES.isPlaygroundLoaded;

    @observable accessor isEditorLoading: boolean = INITIAL_VALUES.isEditorLoading;
    @observable accessor isEditingDraftAgent: boolean = INITIAL_VALUES.isEditingDraftAgent;
    @observable accessor selectedPresenter: string = INITIAL_VALUES.selectedPresenter;
    @observable accessor selectedPresenterType: ImageType = INITIAL_VALUES.selectedPresenterType;

    @observable accessor presenterBackgroundSourceUrl: string = INITIAL_VALUES.presenterBackgroundSourceUrl;
    @observable accessor presenterBackgroundColor: string = INITIAL_VALUES.presenterBackgroundColor;
    @observable accessor presenterBackgroundThumbnailUrl: string = INITIAL_VALUES.presenterBackgroundThumbnailUrl;
    @observable accessor backgroundProcessingPresenters: Set<string> = new Set<string>(INITIAL_VALUES.backgroundProcessingPresenters);

    @observable accessor errors: Errors = { ...INITIAL_VALUES.errors };
    @observable accessor ttsVoiceConfig: VoiceConfig = INITIAL_VALUES.ttsVoiceConfig;
    @observable accessor ttsSelectedLanguage: string | null = INITIAL_VALUES.ttsSelectedLanguage;
    @observable accessor ttsSelectedVoice: IVoice | null = INITIAL_VALUES.ttsSelectedVoice;
    @observable accessor isVoiceManuallySelected: boolean = INITIAL_VALUES.isVoiceManuallySelected;
    @observable accessor showUpgradeModal: boolean = INITIAL_VALUES.showUpgradeModal;
    @observable accessor isAgentEdit: boolean = INITIAL_VALUES.isAgentEdit;

    @observable accessor ui: UI = INITIAL_VALUES.ui;

    constructor(rootStore: RootStore) {
        this.root = rootStore;
    }

    // Computed getters

    @computed get defaultKnowledgeSource() {
        return this.uploadingDocumentFiles.length > 0 ? AgentKnowledgeSource.Documents : AgentKnowledgeSource.BaseKnowledge;
    }

    @computed get shouldHaveKnowledge() {
        return [AgentGrounding.Grounded, AgentGrounding.Hybrid].includes(this.llm.template || DEFAULT_AGENT_GROUNDING);
    }

    @computed get validatedFiles() {
        return this.uploadingDocumentFiles.filter(file => file.status === AgentFileS3UploadStatus.Validated);
    }

    @computed get missingKnowledgeError() {
        const {
            llm: { prompt_customization },
            validatedFiles,
            shouldHaveKnowledge,
            baseKnowledge,
        } = this;

        if (!shouldHaveKnowledge) return;

        if (
            prompt_customization?.knowledge_source === AgentKnowledgeSource.BaseKnowledge &&
            shouldHaveKnowledge &&
            [null, ''].includes(baseKnowledge)
        )
            return KnowledgeErrorType.INPUT_TEXT;

        if (validatedFiles.length === 0 && prompt_customization?.knowledge_source !== AgentKnowledgeSource.BaseKnowledge)
            return KnowledgeErrorType.FILES;
    }

    // Setters

    @action setCurrentTab = (selectedTab: AgentCreateTabType) => {
        this.currentTab = selectedTab;
    };

    @action setIsAgentEdit = (isAgentEdit: boolean) => {
        this.isAgentEdit = isAgentEdit;
    };

    @action setErrorTabs = (errorTabs: ValueOrUpdater<AgentCreateTabType[]>) => {
        let result: AgentCreateTabType[];
        if (typeof errorTabs === 'function') {
            result = errorTabs(this.errorTabs);
        } else {
            result = errorTabs;
        }
        this.errorTabs = result;
    };
    @action setInputAgent = (inputAgent: ValueOrUpdater<IAgent>) => {
        let result: IAgent;
        if (typeof inputAgent === 'function') {
            result = inputAgent(this.inputAgent);
        } else {
            result = inputAgent;
        }
        this.inputAgent = result;
    };
    @action setLLM = (llm: ValueOrUpdater<LLM>) => {
        let result: LLM;
        if (typeof llm === 'function') {
            result = llm(this.llm);
        } else {
            result = llm;
        }
        this.llm = result;
    };

    @action setTriggers = (triggers: ValueOrUpdater<Triggers>) => {
        let result: Triggers;
        if (typeof triggers === 'function') {
            result = triggers(this.triggers);
        } else {
            result = triggers;
        }
        this.triggers = result;
    };
    @action setUploadingDocumentFiles = (uploadingDocumentFiles: ValueOrUpdater<UploadingFile[]>) => {
        let result: UploadingFile[];
        if (typeof uploadingDocumentFiles === 'function') {
            result = uploadingDocumentFiles(this.uploadingDocumentFiles);
        } else {
            result = uploadingDocumentFiles;
        }
        this.uploadingDocumentFiles = result;
    };
    @action setDeletedDocumentFiles = (deletedDocumentFiles: ValueOrUpdater<UploadingFile[]>) => {
        let result: UploadingFile[];
        if (typeof deletedDocumentFiles === 'function') {
            result = deletedDocumentFiles(this.deletedDocumentFiles);
        } else {
            result = deletedDocumentFiles;
        }
        this.deletedDocumentFiles = result;
    };

    @action setIsUpdatingKnowledge = (isUpdatingKnowledge: boolean) => {
        this.isUpdatingKnowledge = isUpdatingKnowledge;
    };
    @action setIsCreating = (isCreating: boolean) => {
        this.isCreating = isCreating;
    };

    @action setPageMode = (pageMode: AgentPageMode) => {
        this.pageMode = pageMode;
    };

    @action setShareActiveTab = (shareActiveTab: AgentShareActiveTab) => {
        this.shareActiveTab = shareActiveTab;
    };

    @action setIsCodeSnippetCopied = (isCodeSnippetCopied: boolean) => {
        this.isCodeSnippetCopied = isCodeSnippetCopied;
    };

    @action setGreetings = (greetings: string[]) => {
        this.greetings = greetings;
    };

    @action setStarterQuestions = (starterQuestions: string[]) => {
        this.starterQuestions = starterQuestions;
    };

    @action setBaseKnowledge = (baseKnowledge: string | null) => {
        this.baseKnowledge = baseKnowledge;
    };

    @action setIsStarterQuestionsEdited = (isStarterQuestionsEdited: boolean) => {
        this.isStarterQuestionsEdited = isStarterQuestionsEdited;
    };

    @action setInitAgentState = (initAgent: ValueOrUpdater<InitAgentState>) => {
        let result: InitAgentState;
        if (typeof initAgent === 'function') {
            result = initAgent(this.initAgentState);
        } else {
            result = initAgent;
        }
        this.initAgentState = result;
    };

    @action setPresenterIsStitch = (presenterIsStich: boolean) => {
        this.presenterIsStitch = presenterIsStich;
    };
    @action setPresenterBackgroundSourceUrl = (presenterBackgroundSourceUrl: string) => {
        this.presenterBackgroundSourceUrl = presenterBackgroundSourceUrl;
    };
    @action setPresenterBackgroundColor = (presenterBackgroundColor: string) => {
        this.presenterBackgroundColor = presenterBackgroundColor;
    };
    @action setPresenterBackgroundThumbnailUrl = (presenterBackgroundThumbnailUrl: string) => {
        this.presenterBackgroundThumbnailUrl = presenterBackgroundThumbnailUrl;
    };

    @action setBackgroundProcessingPresenters = (backgroundProcessingPresenters: ValueOrUpdater<Set<string>>) => {
        let result: Set<string>;
        if (typeof backgroundProcessingPresenters === 'function') {
            result = backgroundProcessingPresenters(this.backgroundProcessingPresenters);
        } else {
            result = backgroundProcessingPresenters;
        }
        this.backgroundProcessingPresenters = result;
    };

    @action setIsPlaygroundLoaded = (isPlaygroundLoaded: boolean) => {
        this.isPlaygroundLoaded = isPlaygroundLoaded;
    };

    @action setIsEditorLoading = (isEditorLoading: boolean) => {
        this.isEditorLoading = isEditorLoading;
    };

    @action setIsEditingDraftAgent = (isEditingDraftAgent: boolean) => {
        this.isEditingDraftAgent = isEditingDraftAgent;
    };

    @action setSelectedPresenter = (selectedPresenter: string) => {
        this.selectedPresenter = selectedPresenter;
    };

    @action setSelectedPresenterType = (selectedPresenterType: ImageType) => {
        this.selectedPresenterType = selectedPresenterType;
    };

    @action setErrors = (errors: ValueOrUpdater<Errors>) => {
        let result: Errors;
        if (typeof errors === 'function') {
            result = errors(this.errors);
        } else {
            result = errors;
        }
        this.errors = result;
    };

    @action setTtsVoiceConfig = (ttsVoiceConfig: ValueOrUpdater<VoiceConfig>) => {
        let result: VoiceConfig;
        if (typeof ttsVoiceConfig === 'function') {
            result = ttsVoiceConfig(this.ttsVoiceConfig);
        } else {
            result = ttsVoiceConfig;
        }
        this.ttsVoiceConfig = result;
    };

    @action setTtsSelectedLanguage = (ttsSelectedLanguage: string | null) => {
        this.ttsSelectedLanguage = ttsSelectedLanguage;
    };

    @action setTtsSelectedVoice = (ttsSelectedVoice: IVoice | null, isVoiceManuallySelected?: boolean) => {
        this.ttsSelectedVoice = ttsSelectedVoice;

        if (typeof isVoiceManuallySelected === 'boolean') {
            this.isVoiceManuallySelected = isVoiceManuallySelected;
        }
    };

    @action setUi = (ui: ValueOrUpdater<UI>) => {
        let result: UI;
        if (typeof ui === 'function') {
            result = ui(this.ui);
        } else {
            result = ui;
        }
        this.ui = result;
    };

    @action setShowUpgradeModal = (showUpgradeModal: boolean) => {
        this.showUpgradeModal = showUpgradeModal;
    };

    // Resters

    @action resetCurrentTab = () => {
        this.currentTab = INITIAL_VALUES.currentTab;
    };

    @action resetErrorTabs = () => {
        this.errorTabs = [...INITIAL_VALUES.errorTabs];
    };

    @action resetInputAgent = () => {
        this.inputAgent = { ...INITIAL_VALUES.inputAgent };
    };

    @action resetLLM = () => {
        this.llm = { ...INITIAL_VALUES.llm };
    };

    @action resetTriggers = () => {
        this.triggers = { ...INITIAL_VALUES.triggers };
    };

    @action resetUploadingDocumentFiles = () => {
        this.uploadingDocumentFiles = [...INITIAL_VALUES.uploadingDocumentFiles];
    };

    @action resetDeletedDocumentFiles = () => {
        this.deletedDocumentFiles = [...INITIAL_VALUES.deletedDocumentFiles];
    };

    @action resetIsUpdatingKnowledge = () => {
        this.isUpdatingKnowledge = INITIAL_VALUES.isUpdatingKnowledge;
    };

    @action resetIsCreating = () => {
        this.isCreating = INITIAL_VALUES.isCreating;
    };

    @action resetPageMode = () => {
        this.pageMode = INITIAL_VALUES.pageMode;
    };

    @action resetShareActiveTab = () => {
        this.shareActiveTab = INITIAL_VALUES.shareActiveTab;
    };

    @action resetIsCodeSnippetCopied = () => {
        this.isCodeSnippetCopied = INITIAL_VALUES.isCodeSnippetCopied;
    };

    @action resetGreetings = () => {
        this.greetings = [...INITIAL_VALUES.greetings];
    };

    @action resetStarterQuestions = () => {
        this.starterQuestions = [...INITIAL_VALUES.starterQuestions];
    };

    @action resetBaseKnowledge = () => {
        this.baseKnowledge = INITIAL_VALUES.baseKnowledge;
    };

    @action resetIsStarterQuestionsEdited = () => {
        this.isStarterQuestionsEdited = INITIAL_VALUES.isStarterQuestionsEdited;
    };

    @action resetInitAgentState = () => {
        this.initAgentState = INITIAL_VALUES.initAgentState;
    };

    @action resetPresenterIsStitch = () => {
        this.presenterIsStitch = INITIAL_VALUES.presenterIsStitch;
    };

    @action resetIsPlaygroundLoaded = () => {
        this.isPlaygroundLoaded = INITIAL_VALUES.isPlaygroundLoaded;
    };

    @action resetIsEditorLoading = () => {
        this.isEditorLoading = INITIAL_VALUES.isEditorLoading;
    };

    @action resetIsEditingDraftAgent = () => {
        this.isEditingDraftAgent = INITIAL_VALUES.isEditingDraftAgent;
    };

    @action resetSelectedPresenter = () => {
        this.selectedPresenter = INITIAL_VALUES.selectedPresenter;
    };

    @action resetSelectedPresenterType = () => {
        this.selectedPresenterType = INITIAL_VALUES.selectedPresenterType;
    };

    @action resetPresenterBackgroundSourceUrl = () => {
        this.presenterBackgroundSourceUrl = INITIAL_VALUES.presenterBackgroundSourceUrl;
    };

    @action resetPresenterBackgroundThumbnailUrl = () => {
        this.presenterBackgroundThumbnailUrl = INITIAL_VALUES.presenterBackgroundThumbnailUrl;
    };

    @action resetPresenterBackgroundColor = () => {
        this.presenterBackgroundColor = INITIAL_VALUES.presenterBackgroundColor;
    };

    @action resetBackgroundProcessingPresenters = () => {
        this.backgroundProcessingPresenters = new Set<string>(INITIAL_VALUES.backgroundProcessingPresenters);
    };

    @action resetErrors = () => {
        this.errors = { ...INITIAL_VALUES.errors };
    };

    @action resetTtsVoiceConfig = () => {
        this.ttsVoiceConfig = INITIAL_VALUES.ttsVoiceConfig;
    };

    @action resetTtsSelectedLanguage = () => {
        this.ttsSelectedLanguage = INITIAL_VALUES.ttsSelectedLanguage;
    };

    @action resetTtsSelectedVoice = () => {
        this.ttsSelectedVoice = INITIAL_VALUES.ttsSelectedVoice;
    };

    @action resetUi = () => {
        this.ui = { ...INITIAL_VALUES.ui };
    };

    @action resetShowUpgradeModal = () => {
        this.showUpgradeModal = INITIAL_VALUES.showUpgradeModal;
    };
}
